import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { MobXProviderContext, observer } from 'mobx-react';
import { Icon, Image } from 'semantic-ui-react';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import NavigationService from '../services/NavigationService';
import PopupService from '../services/PopupService';
import ResourcePacingService from '../services/ResourcePacingService';
import UtilityService from '../services/UtilityService';

import { ELEMENT_TYPE } from '../managers/CourseManager';

import iconArrowCircleRight from '../img/icon-arrow-circle-right.svg';

const VerticalCourseTOCRow = observer((props) => {
  const { ariaLevel, ariaPosInSet, ariaSetSize, courseTOCElement, history, t } = props;
  const {
    courseDeliveryManager,
    courseManager,
    userManager,
    verticalCourseTOCManager
  } = useContext(MobXProviderContext);

  const ChildVerticalCourseTOCRow = useMemo(() => SatCoreComponent('VerticalCourseTOCRow'), []);

  const { CourseResourceElements: childCourseTOCElements = [] } = courseTOCElement;

  const isBranch = ELEMENT_TYPE.isBranchType(courseTOCElement);
  const { isStudent } = userManager;
  const showHidden = courseDeliveryManager.shouldShowHiddenResourcesObservable;
  const visibilityMode = ResourcePacingService.visibilityMode(courseTOCElement);
  const isTeacherOnly = courseTOCElement.deliveryMode === 'teacher_only';
  const isStudentTeacherOnly = isStudent && isTeacherOnly;
  const isHiddenFromStudents = visibilityMode === 'hidden_from_students';
  const shouldHideBranch = isStudentTeacherOnly || (!showHidden && isHiddenFromStudents && !isTeacherOnly);

  if (!isBranch || shouldHideBranch) {
    return null;
  }

  const isExpanded = verticalCourseTOCManager.verticalCourseTOCExpandedIds.has(courseTOCElement.id);
  const isSelected = courseManager.currentElementId === courseTOCElement.id;
  const hasBranchChildren = childCourseTOCElements.some((childCourseTOCElement) => {
    return ELEMENT_TYPE.isBranchType(childCourseTOCElement);
  });

  // Fallback label when the element has no name (backend sometimes omits it).
  // Uses shared type translations from `ClassDetails` so satellite overrides
  // (e.g. for Unit/Section/etc) can have satellite-specific translations.
  const getFallbackRowLabel = () => {
    switch (courseTOCElement.type) {
      case ELEMENT_TYPE.UNIT: return t('defaultRowLabelUnit', { orderNumber: ariaPosInSet });
      case ELEMENT_TYPE.SECTION: return t('defaultRowLabelSection', { orderNumber: ariaPosInSet });
      case ELEMENT_TYPE.LESSON: return t('defaultRowLabelLesson', { orderNumber: ariaPosInSet });
      default: return t('defaultRowLabelGeneric', { orderNumber: ariaPosInSet });
    }
  };
  const rowName = courseTOCElement.name?.trim() || getFallbackRowLabel();

  const parsedTitle = UtilityService.reactHtmlParserWrapper(rowName);
  const cleanedTitle = parsedTitle.stripped;
  const popupId = `vertical-course-toc-popup-${courseTOCElement.id}`;

  const handleClickRow = async (event) => {
    courseManager.setTreeLoading(true);
    (!isExpanded && hasBranchChildren) && handleExpandOrCollapseRow(event);
    await NavigationService.handleCourseElementClick(courseTOCElement, history);
    courseManager.setTreeLoading(false);
  };

  const handleExpandOrCollapseRow = (event) => {
    event.stopPropagation();
    verticalCourseTOCManager.toggleVerticalCourseTOCExpanded(courseTOCElement.id);
  };

  const handleLevelKeyDown = (event) => {
    // Stop Enter/Space from bubbling to the row's handleKeyDown, so keyboard-activating
    // the chevron only toggles expand/collapse (matches mouse-click behavior on the chevron).
    // Arrow keys still bubble so ArrowLeft/ArrowRight can drive expand/collapse.
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        event.stopPropagation();
        handleClickRow(event);
        break;
      case 'ArrowRight':
        if (!isExpanded && hasBranchChildren) {
          event.preventDefault();
          event.stopPropagation();
          verticalCourseTOCManager.toggleVerticalCourseTOCExpanded(courseTOCElement.id);
        }
        break;
      case 'ArrowLeft':
        if (isExpanded) {
          event.preventDefault();
          event.stopPropagation();
          verticalCourseTOCManager.toggleVerticalCourseTOCExpanded(courseTOCElement.id);
        }
        break;
      default:
        break;
    }
  };

  const rowLabel = (
    <div
      aria-describedby={popupId}
      className='row-label'>
      {parsedTitle.parsedNoPWrap}
    </div>
  );

  const popupContent = (
    <div className='vertical-course-toc-popup-content'>
      {parsedTitle.parsedNoPWrap}
    </div>
  );

  const elementTypeClassName = courseTOCElement.type
    ? `element-type-${courseTOCElement.type.toLowerCase()}`
    : null;

  const rowClassName = classNames(
    'vertical-course-toc-row',
    'element-type',
    elementTypeClassName, {
      expanded: isExpanded,
      selected: isSelected
    }
  );

  return (
    <li
      aria-current={isSelected ? 'page' : undefined}
      aria-expanded={hasBranchChildren ? isExpanded : undefined}
      aria-level={ariaLevel}
      aria-posinset={ariaPosInSet}
      aria-selected={isSelected}
      aria-setsize={ariaSetSize}
      className={rowClassName}
      data-course-toc-element-id={courseTOCElement.id}
      onKeyDown={handleKeyDown}
      role='treeitem'
      style={{ '--vertical-course-toc-row-depth': ariaLevel }}
      tabIndex={0}>
      <div className='vertical-course-toc-row-content' onClick={handleClickRow}>
        {hasBranchChildren ? (
          <button
            aria-label={isExpanded
              ? t('collapseNode', 'Collapse {{name}}', { name: cleanedTitle })
              : t('expandNode', 'Expand {{name}}', { name: cleanedTitle })}
            className='row-level'
            onClick={handleExpandOrCollapseRow}
            onKeyDown={handleLevelKeyDown}
            type='button'>
            <Icon name={isExpanded ? 'chevron down' : 'chevron right'} />
          </button>
        ) : (
          <span aria-hidden='true' className='row-level' />
        )}
        {PopupService.renderPopup({
          content: popupContent,
          key: popupId,
          mouseEnterDelay: 500,
          trigger: rowLabel
        })}
        <Image alt='' className='row-arrow' src={iconArrowCircleRight} />
      </div>
      {isExpanded && hasBranchChildren && (
        <ul className='vertical-course-toc-children' role='group'>
          {childCourseTOCElements.map((childCourseTOCElement, index) => {
            return (
              <ChildVerticalCourseTOCRow
                key={childCourseTOCElement.id}
                ariaLevel={ariaLevel + 1}
                ariaPosInSet={index + 1}
                ariaSetSize={childCourseTOCElements.length}
                courseTOCElement={childCourseTOCElement}
                history={history} />
            );
          })}
        </ul>
      )}
    </li>
  );
});

export default VerticalCourseTOCRow;
SatCoreRegister('VerticalCourseTOCRow', VerticalCourseTOCRow);
